import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCurrentTheme, Theme } from "../../themes/themeConfig";
import { usePermission } from "../../hooks/usePermission";
import { RootState } from "../../redux/reducers/rootReducer";
import ROUTE_CONFIG from "../../config/routeConfig";
import { VIEW_ACTION_MAP } from "../../config/permissionConfig";
import { buildPortalUrl, isAtlasHost, PORTAL_ROOT, isLocalDev } from "../../config/domain";
import "./Sidebar.css";

interface SidebarProps {
  onToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const { hasPermission } = usePermission();
  const isAtlas = isAtlasHost();
  const meStatus = useSelector((state: RootState) => state.permission.me.status);
  const permissionLoading = meStatus === "loading" || meStatus === "idle";

  const filteredMenuItems = permissionLoading ? [] : ROUTE_CONFIG
    .filter((item) => item.showInMenu !== false)
    .filter((item) => hasPermission(`${item.id}.${VIEW_ACTION_MAP[item.id]}`, item.system));

  useEffect(() => {
    const updateTheme = () => {
      setCurrentTheme(getCurrentTheme());
      applySidebarStyles();
    };

    // Apply sidebar styles on mount
    applySidebarStyles();

    // Listen for custom theme change events
    window.addEventListener('themeChanged', updateTheme);
    
    return () => {
      window.removeEventListener('themeChanged', updateTheme);
    };
  }, []);

  const applySidebarStyles = () => {
    const theme = getCurrentTheme();
    
    // Don't apply custom sidebar styling for Black/Default theme
    if (theme.name === 'default') {
      // Remove any custom styling to use CloudScape defaults
      setTimeout(() => {
        const sidebarSelectors = [
          '.awsui-side-navigation',
          '[data-testid="side-navigation"]',
          'nav'
        ];
        
        sidebarSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('background-color');
              element.style.removeProperty('border-right-color');
              element.style.removeProperty('color');
            }
          });
        });

        const itemSelectors = [
          '.awsui-side-navigation-item',
          '.awsui-side-navigation a',
          '.awsui-side-navigation button'
        ];
        
        itemSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('color');
              element.style.removeProperty('background-color');
            }
          });
        });
      }, 100);
      return;
    }
    
    setTimeout(() => {
      // Target sidebar container
      const sidebarSelectors = [
        '.awsui-side-navigation',
        '[data-testid="side-navigation"]',
        'nav'
      ];
      
      sidebarSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('background-color', theme.colors.navigationBackground, 'important');
            element.style.setProperty('border-right-color', theme.colors.navigationBorder, 'important');
            element.style.setProperty('color', theme.colors.navigationText, 'important');
          }
        });
      });

      // Target sidebar items
      const itemSelectors = [
        '.awsui-side-navigation-item',
        '.awsui-side-navigation a',
        '.awsui-side-navigation button'
      ];
      
      itemSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('color', theme.colors.navigationText, 'important');
            element.style.setProperty('background-color', 'transparent', 'important');
          }
        });
      });
    }, 100);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href;
  };

  const themeClass =
    currentTheme.name === "default"
      ? "sidebar-theme-default"
      : "sidebar-theme-light";

  return (
    <div
      className={`custom-sidebar ${themeClass}`}
      style={{
        height: '100%',
        ...(currentTheme.name !== 'default' && {
          backgroundColor: currentTheme.colors.navigationBackground,
          borderRight: `1px solid ${currentTheme.colors.navigationBorder}`,
          color: currentTheme.colors.navigationText
        })
      }}
    >
      <div className="sidebar-brand">
        <span className="sidebar-brand-text" onClick={() => { if (!isAtlas) navigate("/"); }}>
          Home
        </span>
        {onToggle && (
          <span className="sidebar-collapse-btn" onClick={onToggle} title="Collapse sidebar">
            &#x2039;
          </span>
        )}
      </div>

      <div className="sidebar-divider" />

      {/* <div className="sidebar-section-label">Navigation</div> */}

      <nav className="sidebar-nav">
        {permissionLoading ? (
          <div className="sidebar-skeleton">
            {[1, 2, 3, 4, 5,6,7].map((i) => (
              <div key={i} className="sidebar-skeleton-item" />
            ))}
          </div>
        ) : (
          filteredMenuItems.map((item) => (
            <div
              key={item.href}
              className={`sidebar-nav-item ${isActive(item.href) ? "active" : ""}`}
              data-testid={`sidebar-nav-item-${item.href.replace("/", "")}`}
              onClick={() => navigate(item.href)}
            >
              <span className="sidebar-nav-text">{item.label}</span>
            </div>
          ))
        )}
      </nav>

      {(isLocalDev() || window.location.hostname.endsWith(PORTAL_ROOT)) && (
        <>
          <div className="sidebar-divider" />
          <div className="sidebar-back-hub">
            <a href={buildPortalUrl()} className="sidebar-back-hub-link">
              &#x2190; Back to Hub
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;
